<template>
  <div class="modal-container">
    <div class="blur-backdrop" @click="close" />
    <div class="modal space-between-modal">
      <div class="title danger">
        Are you sure you want to cancel your subscription?
      </div>
      <div class="explanatory-text flex">
        Your team will be disbanded at the end of your current billing cycle. 
        You'll lose all team-related info, 
        including custom prompts, 
        custom prompt packs, and scores. 
        This action cannot be undone! 
      </div>
      <SpinnerButton
        class="button danger center"
        :isProcessing="isProcessingPayment"
        @click="cancelSubscription"
      >
        Cancel Subscription
      </SpinnerButton>
      <SpinnerButton
        class="button center"
        :isDisabled="isProcessingPayment"
        @click="close"
      >
        Go Back
      </SpinnerButton>
    </div>
  </div>
</template>

<script>
import Vue from 'vue';
import SpinnerButton from '@/components/utilComponents/SpinnerButton';

export default Vue.extend({
  name: 'CancelSubscriptionModal',
  components: {
    SpinnerButton
  },
  data: () => ({
    subscriptionInfo: {},
    stripeCustomerId: null,
    socket: null,
    isProcessingPayment: false
  }),
  computed: {
    auth0UserId: function () {
      return this.$auth.user.sub.replace(/\|/g, '_');
    },
    idToken: function () {
      return this.$auth.idToken;
    },
  },
  methods: {
    async cancelSubscription() {
      if (!this.isProcessingPayment) {
        try {
          this.isProcessingPayment = true;

          await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/cancelsubscription`, {
            auth0UserId: this.auth0UserId
          }, {
            headers: {
              'Content-Type': 'application/json', 
              'Authorization': `Bearer ${this.idToken}`
            }
          });

          await this.listenForUpdate();
        } catch (error) {
          console.log('Error:', error);
          this.isProcessingPayment = false;
        }
      }
    },
    async listenForUpdate() {
      await this.getStripeCustomerId();
      this.connectSocket();
      await this.validateSubscriptionUpdate();
    },
    async getStripeCustomerId() {
      const response = await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/getstripecustomerid`, {
        auth0UserId: this.auth0UserId
      }, {
        headers: {
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${this.idToken}`
        }
      });

      this.stripeCustomerId = response.data.stripeCustomerId;

      if (!this.stripeCustomerId) {
        // NOTE: Stripe customer ID should never be undefined here, so there's a backend problem -- refresh page
        this.$router.go();
      }
    },
    async getSubscriptionInfo() {
      const response = await this.axios.post(`${ process.env.VUE_APP_BACKEND_API_URL }/getsubscriptioninfo`, {
        auth0UserId: this.auth0UserId
      }, {
        headers: {
          'Content-Type': 'application/json', 
          'Authorization': `Bearer ${this.idToken}`
        }
      });

      this.subscriptionInfo = response.data.subscriptionInfo;
    },
    async validateSubscriptionUpdate() {
      await this.getSubscriptionInfo();

      if (this.subscriptionInfo.cancelAtPeriodEnd) {
        this.$router.go();
      }
    },
    connectSocket() {
      const newSocket = new WebSocket(`${ process.env.VUE_APP_BACKEND_SOCKET_URL }`);
      newSocket.onopen = () => {
        this.socket = newSocket;
        this.socket.onmessage = (event) => {
          const socketData = JSON.parse(event.data);

          const socketHandlerTable = {
            'subscriptionUpdated': () => {
              if (socketData.stripeCustomerId === this.stripeCustomerId) {
                this.validateSubscriptionUpdate();
              }
            }
          };

          if (socketData.code in socketHandlerTable) {
            socketHandlerTable[socketData.code]();
          }
        };

        this.socket.send(JSON.stringify({
          action: 'attachSubscriptionSocket',
          stripeCustomerId: this.stripeCustomerId
        }));
      };
    },
    disconnectSocket() {
      try {
        this.socket.send(JSON.stringify({
          action: 'detachSubscriptionSocket',
          stripeCustomerId: this.stripeCustomerId
        }));

        this.socket.close();
      } catch (error) {
        console.log('Error on socket disconnect:', error);
      }
    },
    close() {
      if (this.isProcessingPayment) {
        return;
      }
      this.$emit('close');
    }
  }
})
</script>

<style scoped lang="scss">
@import '@/styles/modal-styles.scss';
</style>
